import type { MecaHistoryEntry, Valve } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { Dialog } from "@eisox/design-system";
import { isFeatureSupported } from "@eisox/device-features";
import { HistoryIcon } from "@eisox/icons";
import { calcRodStroke } from "@eisox/valves";

import { valveApi } from "~/api";
import { formatTrigCurThreshold } from "~/helpers/valve.helper";
import { m } from "~/paraglide/messages";

import { type ValveHistoryColumn, ValveHistoryTable } from "./valve-history-table";

interface MecaHistoryDialogProps extends Dialog.RootProps {
  buildingId: string;
  valve: Pick<Valve, "id" | "softwareVersion">;
}

export const MecaHistoryDialog: React.FC<MecaHistoryDialogProps> = ({ buildingId, valve, children, ...props }) => {
  const supportsTrigCur = isFeatureSupported("valve", "TRIG_CUR", valve.softwareVersion);

  const columns: ValveHistoryColumn<MecaHistoryEntry>[] = [
    { id: "date", header: m.date(), widthPercent: 22, cell: entry => dayjs(entry.date).format("L LT") },
    { id: "openPos", header: m.meca_open(), widthPercent: 8, cell: entry => entry.openPos },
    { id: "closePos", header: m.meca_close(), widthPercent: 8, cell: entry => entry.closePos },
    {
      id: "rodStroke",
      header: m.meca_course(),
      widthPercent: 14,
      cell: entry =>
        entry.openPos !== undefined && entry.closePos !== undefined
          ? calcRodStroke(entry.openPos, entry.closePos)
          : "-",
    },
    { id: "problem", header: m.problem(), widthPercent: 14, cell: entry => entry.problem },
    ...(supportsTrigCur
      ? [
          {
            id: "trigCur",
            header: m.meca_trig_cur_threshold(),
            widthPercent: 14,
            cell: (entry: MecaHistoryEntry) => formatTrigCurThreshold(entry.trigCur),
          },
        ]
      : []),
    {
      id: "softwareVersion",
      header: m.software_version(),
      widthPercent: supportsTrigCur ? 20 : 34,
      cell: entry => entry.softwareVersion,
    },
  ];

  return (
    <Dialog.Root {...props}>
      {children ? <Dialog.Trigger asChild>{children}</Dialog.Trigger> : null}
      <Dialog.Content
        title={m.meca_history()}
        icon={<HistoryIcon />}
        className="gap-4 md:gap-6 flex h-[85dvh] max-h-[900px] w-full flex-col min-[992px]:w-[80%] min-[1200px]:w-[70%] min-[1400px]:w-[60%]"
      >
        <ValveHistoryTable
          columns={columns}
          query={filters => valveApi.queries.mecaHistory(buildingId, valve.id, filters)}
        />
      </Dialog.Content>
    </Dialog.Root>
  );
};
